import { Bean, Autowired } from './context/context';
import { BeanStub } from "./context/beanStub";
import { addCssClass } from './utils/dom';
import { doOnce } from './utils/function';

export type SASS_PROPERTIES = 'headerHeight' | 'headerCellMinWidth' | 'listItemHeight' | 'rowHeight' | 'chartMenuPanelWidth';

interface HardCodedSize {
  [key: string]: {
    [key in SASS_PROPERTIES]?: number;
  };
}

const BASE_GRID_SIZE = 4;
const DEFALUT_GRID_SIZE = 6;

const HARD_CODED_SIZES: HardCodedSize = {
  // this item is required for custom themes
  'dy-theme-custom': {
    headerHeight: 25,
    headerCellMinWidth: 24,
    listItemHeight: BASE_GRID_SIZE * 5,
    rowHeight: 25,
    chartMenuPanelWidth: 220
  },
  'dy-theme-default': {
    headerHeight: DEFALUT_GRID_SIZE * 8,
    headerCellMinWidth: 36,
    listItemHeight: DEFALUT_GRID_SIZE * 4,
    rowHeight: DEFALUT_GRID_SIZE * 7,
    chartMenuPanelWidth: 240
  }
};

const SASS_PROPERTY_BUILDER: { [key in SASS_PROPERTIES]: string[] } = {
  headerHeight: ['dy-header-row'],
  headerCellMinWidth: ['dy-header-cell'],
  listItemHeight: ['dy-virtual-list-item'],
  rowHeight: ['dy-row'],
  chartMenuPanelWidth: ['dy-chart-docked-container']
};

const CALCULATED_SIZES: HardCodedSize = {};

@Bean('environment')
export class Environment extends BeanStub {

  @Autowired('eGridDiv') private eGridDiv: HTMLElement;

  public getSassVariable(theme: string, key: SASS_PROPERTIES): number | undefined {
    const useTheme = 'dy-theme-' + (theme.match('default') ? 'default' : 'custom');
    const defaultValue = HARD_CODED_SIZES[useTheme][key];
    let calculatedValue = 0;
    if (!CALCULATED_SIZES[theme]) {
      CALCULATED_SIZES[theme] = {};
    }

    const size = CALCULATED_SIZES[theme][key];

    if (size != null) {
      return size;
    }

    if (SASS_PROPERTY_BUILDER[key]) {
      const classList = SASS_PROPERTY_BUILDER[key];
      const div = document.createElement('div');
      addCssClass(div, theme);
      div.style.position = 'absolute';

      const el: HTMLDivElement = classList.reduce((prevEl: HTMLDivElement, currentClass: string) => {
        const currentDiv = document.createElement('div');
        currentDiv.style.position = 'static';
        addCssClass(currentDiv, currentClass);
        prevEl.appendChild(currentDiv);

        return currentDiv;
      }, div);

      if (document.body) {
        document.body.appendChild(div);
        const sizeName = key.toLowerCase().indexOf('height') !== -1 ? 'height' : 'width';
        calculatedValue = parseInt(window.getComputedStyle(el)[sizeName]!, 10);
        document.body.removeChild(div);
      }
    }

    CALCULATED_SIZES[theme][key] = calculatedValue || defaultValue;

    return CALCULATED_SIZES[theme][key];
  }

  public isThemeDark(): boolean {
    const { theme } = this.getTheme();
    return !!theme && theme.indexOf('dark') >= 0;
  }

  public chartMenuPanelWidth() {
    const theme = this.getTheme().themeFamily;
    return this.getSassVariable(theme!, 'chartMenuPanelWidth');
  }

  public getTheme(): { theme?: string; el?: HTMLElement; themeFamily?: string; } {
    const reg = /\bdy-(material|(?:theme-([\w\-]*)))\b/;
    let el: HTMLElement | undefined = this.eGridDiv;
    let themeMatch: RegExpMatchArray | null = null;

    while (el) {
      themeMatch = reg.exec(el.className);
      if (!themeMatch) {
        el = el.parentElement || undefined;
      } else {
        break;
      }
    }

    if (!themeMatch) { return {}; }

    const theme = themeMatch[0];
    const usingOldTheme = themeMatch[2] === undefined;

    if (usingOldTheme) {
      const newTheme = theme.replace('dy-', 'dy-theme-');
      doOnce(() => console.warn(`DYNA Grid: As of v19 old theme are no longer provided. Please replace ${theme} with ${newTheme}.`), 'using-old-theme');
    }

    return { theme, el, themeFamily: theme.replace(/-dark$/, '') };
  }
}
